import React, { useState, useEffect, useCallback } from 'react';
import Style from './style.module.css';
import 'primeicons/primeicons.css';
import axios from 'axios';
import Spinner from '../shared/Spinner/index.js';
import ModalWrapper from '../shared/ModalWrapper/wrapper';
import MailingListManager, { modalStyles } from './MailingListManager.js';
import GoogleSignInButton, {
  disableGoogleAutoSelect,
} from './GoogleSignInButton.js';

import {
  signInAdminWithGoogle,
  signOutAdmin,
  getPowerBiUrl,
  updatePowerBiUrl,
  adminAuthenticated,
  runWeeklySubscriptions,
  weeklySubscriptionJob,
  weeklySubscriptions,
  sendWeeklySubscription,
  setWeeklySubscriptionPaused,
  deleteWeeklySubscription,
} from '../../gql';

const graphqlRequest = (query, variables, adminToken) =>
  axios.post(
    process.env.REACT_APP_GRAPHQL_URI,
    { query, variables },
    {
      withCredentials: true,
      headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {},
    },
  );

const getApiBaseUrl = () => {
  const graphqlUri =
    process.env.REACT_APP_GRAPHQL_URI || 'http://localhost:4000/graphql';
  return graphqlUri.replace(/\/graphql\/?$/, '');
};

const triggerDownload = (url, filename) => {
  const link = document.createElement('a');
  link.href = url;
  if (filename) link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const downloadBlob = (blob, filename) => {
  const blobUrl = URL.createObjectURL(blob);
  triggerDownload(blobUrl, filename);
  URL.revokeObjectURL(blobUrl);
};

const AdminPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminToken, setAdminToken] = useState(null);
  const [adminEmail, setAdminEmail] = useState(null);
  const [error, setError] = useState(false);
  const [currentUrl, setCurrentUrl] = useState(false);
  const [newURL, setNewURL] = useState('');
  const [updateMessage, setUpdateMessage] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportMessage, setReportMessage] = useState('');
  const [isRunningSubscriptions, setIsRunningSubscriptions] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState('');
  const [subscriptionIsError, setSubscriptionIsError] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(false);
  const [sendingSubscriptionId, setSendingSubscriptionId] = useState(null);
  const [updatingSubscriptionId, setUpdatingSubscriptionId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchCurrentUrl = useCallback(
    async (token = adminToken) => {
      setIsLoading(true);

      try {
        const response = await graphqlRequest(getPowerBiUrl, undefined, token);
        const url = response.data?.data?.powerBiUrl;

        if (url) setCurrentUrl(url);
      } catch (err) {
        console.error('Error fetching URL:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [adminToken],
  );

  const fetchSubscriptions = useCallback(
    async (token = adminToken) => {
      setIsLoadingSubscriptions(true);

      try {
        const response = await graphqlRequest(
          weeklySubscriptions,
          undefined,
          token,
        );
        setSubscriptions(response.data?.data?.weeklySubscriptions || []);
      } catch (err) {
        console.error('Error fetching subscriptions:', err);
      } finally {
        setIsLoadingSubscriptions(false);
      }
    },
    [adminToken],
  );

  useEffect(() => {
    const checkSession = async () => {
      setIsLoading(true);

      try {
        const response = await graphqlRequest(
          adminAuthenticated,
          undefined,
          adminToken,
        );
        const authenticated = response.data?.data?.adminAuthenticated;

        if (authenticated) {
          setIsAuthenticated(true);
          setAdminEmail(response.data?.data?.adminEmail || null);
          await fetchCurrentUrl(adminToken);
          await fetchSubscriptions(adminToken);
          return;
        }
      } catch (err) {
        console.error('Session check error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();
  }, [adminToken, fetchCurrentUrl, fetchSubscriptions]);

  const updateUrl = async () => {
    setUpdateMessage('');
    setIsLoading(true);

    try {
      const response = await graphqlRequest(
        updatePowerBiUrl,
        { url: newURL },
        adminToken,
      );

      const result = response.data?.data?.updatePowerBiUrl;

      if (result?.success) {
        setUpdateMessage('URL updated successfully!');
        setCurrentUrl(result.url);
        setNewURL('');
      } else {
        setUpdateMessage(`${result?.message || 'Failed to update URL'}`);
      }
    } catch (err) {
      console.error(err);
      const message =
        err.response?.data?.errors?.[0]?.message ||
        'Failed to update URL. Please try again.';
      setUpdateMessage(message);

      if (message === 'Unauthorized') {
        setIsAuthenticated(false);
        setAdminToken(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const downloadWeeklyReport = async () => {
    setReportMessage('');
    setIsGeneratingReport(true);

    const authHeaders = adminToken
      ? { Authorization: `Bearer ${adminToken}` }
      : {};
    const requestConfig = {
      withCredentials: true,
      headers: authHeaders,
    };

    try {
      const startResponse = await axios.post(
        `${getApiBaseUrl()}/admin/philly-weekly-report`,
        {},
        requestConfig,
      );
      const { jobId } = startResponse.data;

      const pollIntervalMs = 3000;
      const maxPolls = Math.floor((60 * 60 * 1000) / pollIntervalMs);
      let polls = 0;

      let job;
      do {
        await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));

        const statusResponse = await axios.get(
          `${getApiBaseUrl()}/admin/philly-weekly-report/${jobId}`,
          requestConfig,
        );
        job = statusResponse.data;

        polls += 1;
        if (polls > maxPolls) {
          throw new Error('Report generation timed out. Please try again.');
        }
      } while (job.status !== 'complete' && job.status !== 'failed');

      if (job.status === 'failed') {
        throw new Error(job.error || 'Failed to generate weekly report');
      }

      const fileResponse = await axios.get(
        `${getApiBaseUrl()}${job.downloadUrl}`,
        {
          responseType: 'blob',
          withCredentials: true,
        },
      );

      downloadBlob(
        fileResponse.data,
        job.filename || 'philly_weekly_report.pptx',
      );
      setReportMessage('Weekly report downloaded.');
    } catch (err) {
      console.error('Weekly report error:', err);

      const message =
        err.response?.data?.error ||
        err.message ||
        'Failed to generate weekly report. Please try again.';

      setReportMessage(message);

      if (message === 'Unauthorized') {
        setIsAuthenticated(false);
        setAdminToken(null);
      }
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const pollSubscriptionJob = async (jobId) => {
    const pollIntervalMs = 3000;
    const maxPolls = Math.floor((60 * 60 * 1000) / pollIntervalMs);
    let polls = 0;

    let job;
    do {
      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));

      const statusResponse = await graphqlRequest(
        weeklySubscriptionJob,
        { jobId },
        adminToken,
      );

      if (statusResponse.data.errors) {
        throw new Error(statusResponse.data.errors[0].message);
      }

      job = statusResponse.data.data?.weeklySubscriptionJob;
      if (!job) {
        throw new Error('Weekly subscription job not found');
      }

      polls += 1;
      if (polls > maxPolls) {
        throw new Error('Weekly emails timed out. Please try again.');
      }
    } while (job.status !== 'complete' && job.status !== 'failed');

    if (job.status === 'failed') {
      throw new Error(job.error || 'Failed to send weekly subscription emails');
    }

    return job;
  };

  const runWeeklySubscriptionEmails = async () => {
    setSubscriptionMessage('');
    setSubscriptionIsError(false);
    setIsRunningSubscriptions(true);

    try {
      const startResponse = await graphqlRequest(
        runWeeklySubscriptions,
        undefined,
        adminToken,
      );

      if (startResponse.data.errors) {
        throw new Error(startResponse.data.errors[0].message);
      }

      const jobId = startResponse.data.data?.runWeeklySubscriptions?.jobId;
      if (!jobId) {
        throw new Error('Failed to start weekly subscription emails');
      }

      const job = await pollSubscriptionJob(jobId);

      setSubscriptionMessage(job.message || 'Weekly subscription emails sent.');
      setSubscriptionIsError(false);
    } catch (err) {
      console.error('Weekly subscription job error:', err);

      const message =
        err.message ||
        'Failed to send weekly subscription emails. Please try again.';

      setSubscriptionMessage(message);
      setSubscriptionIsError(true);

      if (message === 'Unauthorized') {
        setIsAuthenticated(false);
        setAdminToken(null);
      }
    } finally {
      setIsRunningSubscriptions(false);
    }
  };

  const sendSubscriptionNow = async (subscription) => {
    setSubscriptionMessage('');
    setSubscriptionIsError(false);
    setSendingSubscriptionId(subscription.id);

    try {
      const startResponse = await graphqlRequest(
        sendWeeklySubscription,
        { id: subscription.id },
        adminToken,
      );

      if (startResponse.data.errors) {
        throw new Error(startResponse.data.errors[0].message);
      }

      const jobId = startResponse.data.data?.sendWeeklySubscription?.jobId;
      if (!jobId) {
        throw new Error('Failed to start subscription email');
      }

      const job = await pollSubscriptionJob(jobId);

      setSubscriptionMessage(
        job.message || `Sent weekly report to ${subscription.email}.`,
      );
      setSubscriptionIsError((job.failed || 0) > 0);
    } catch (err) {
      console.error('Send subscription error:', err);

      const message =
        err.message || 'Failed to send subscription email. Please try again.';

      setSubscriptionMessage(message);
      setSubscriptionIsError(true);

      if (message === 'Unauthorized') {
        setIsAuthenticated(false);
        setAdminToken(null);
      }
    } finally {
      setSendingSubscriptionId(null);
    }
  };

  const handleSubscriptionError = (err, fallback) => {
    console.error(fallback, err);
    const message =
      err.response?.data?.errors?.[0]?.message || err.message || fallback;

    if (message === 'Unauthorized') {
      setIsAuthenticated(false);
      setAdminToken(null);
    }
    return message;
  };

  const toggleSubscriptionPaused = async (subscription) => {
    setSubscriptionMessage('');
    setSubscriptionIsError(false);
    setUpdatingSubscriptionId(subscription.id);

    try {
      const response = await graphqlRequest(
        setWeeklySubscriptionPaused,
        { id: subscription.id, paused: !subscription.paused },
        adminToken,
      );

      if (response.data.errors) {
        throw new Error(response.data.errors[0].message);
      }

      const { paused } = response.data.data.setWeeklySubscriptionPaused;
      setSubscriptions((current) =>
        current.map((item) =>
          item.id === subscription.id ? { ...item, paused } : item,
        ),
      );
      setSubscriptionMessage(
        `${paused ? 'Paused' : 'Resumed'} subscription for ${subscription.email}.`,
      );
    } catch (err) {
      setSubscriptionMessage(
        handleSubscriptionError(err, 'Failed to update subscription.'),
      );
      setSubscriptionIsError(true);
    } finally {
      setUpdatingSubscriptionId(null);
    }
  };

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setPendingDelete(null);
    setDeleteError('');
  };

  const confirmDeleteSubscription = async (event) => {
    event.preventDefault();
    if (!pendingDelete) return;

    setDeleteError('');
    setIsDeleting(true);

    try {
      const response = await graphqlRequest(
        deleteWeeklySubscription,
        { id: pendingDelete.id },
        adminToken,
      );

      if (response.data.errors) {
        throw new Error(response.data.errors[0].message);
      }

      setSubscriptions((current) =>
        current.filter((item) => item.id !== pendingDelete.id),
      );
      setSubscriptionMessage(
        `Deleted subscription for ${pendingDelete.email}.`,
      );
      setSubscriptionIsError(false);
      setPendingDelete(null);
    } catch (err) {
      setDeleteError(
        handleSubscriptionError(err, 'Failed to delete subscription.'),
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const signInWithGoogle = async (credential) => {
    setIsLoading(true);
    setError('');

    try {
      const response = await graphqlRequest(
        signInAdminWithGoogle,
        { credential },
        null,
      );

      const result = response.data?.data?.signInAdminWithGoogle;

      if (result?.success) {
        setAdminToken(result.token || null);
        setAdminEmail(result.email || null);
        setIsAuthenticated(true);
        await fetchCurrentUrl(result.token || null);
        await fetchSubscriptions(result.token || null);
      } else {
        setError(result?.message || 'Google sign-in failed');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(
        err.response?.data?.errors?.[0]?.message || 'Google sign-in failed',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      await graphqlRequest(signOutAdmin, undefined, null);
    } catch (err) {
      console.error('Sign out error:', err);
    }
    disableGoogleAutoSelect();
    setAdminToken(null);
    setAdminEmail(null);
    setIsAuthenticated(false);
  };

  if (isLoading) return <Spinner />;

  if (!isAuthenticated) {
    return (
      <div className={Style.container}>
        {error && (
          <div className={Style.errorMessage}>
            <span className={Style.errorIcon}>!</span>
            {error}
          </div>
        )}
        <GoogleSignInButton onCredential={signInWithGoogle} />
      </div>
    );
  }

  return (
    <div className={Style.container}>
      <div className={Style.signedInBar}>
        <span>Signed in as {adminEmail || 'admin'}</span>
        <button type="button" className={Style.linkButton} onClick={signOut}>
          Sign out
        </button>
      </div>

      <div className={Style.section}>
        <label className={Style.label}>Current PowerBI URL:</label>
        <div className={Style.currentUrl}>
          {currentUrl || 'No URL configured'}
        </div>
      </div>
      <div className={Style.section}>
        <label className={Style.label}>New PowerBI URL:</label>
        <input
          className={Style.inputText}
          id="url"
          type="text"
          placeholder="https://app.powerbi.com/view/..."
          value={newURL}
          onChange={(e) => setNewURL(e.target.value)}
          required
        />
        <div className={Style.hint}>
          Must start with: https://app.powerbi.com/view
        </div>
      </div>

      {updateMessage && (
        <div
          className={
            updateMessage.includes('successfully')
              ? Style.successMessage
              : Style.errorMessage
          }
        >
          {updateMessage}
        </div>
      )}

      <div
        disabled={isLoading}
        className={Style.submitButton}
        onClick={updateUrl}
      >
        Update
      </div>

      <div className={Style.divider} />

      <MailingListManager request={graphqlRequest} adminToken={adminToken} />

      <div className={Style.divider} />

      <div className={Style.section}>
        <label className={Style.label}>Weekly Report:</label>
        <div className={Style.reportHint}>
          Generates the standard Philadelphia weekly shooting report and
          downloads it as a PowerPoint file.
        </div>
      </div>

      {reportMessage && (
        <div
          className={
            reportMessage.includes('downloaded')
              ? Style.successMessage
              : Style.errorMessage
          }
        >
          {reportMessage}
        </div>
      )}

      <button
        type="button"
        disabled={isGeneratingReport}
        className={Style.submitButton}
        onClick={downloadWeeklyReport}
      >
        {isGeneratingReport ? 'Generating…' : 'Download Weekly Report'}
      </button>

      <div className={Style.divider} />

      <div className={Style.section}>
        <label className={Style.label}>Weekly Subscription Emails:</label>
        <div className={Style.reportHint}>
          Regenerates every saved subscription and emails the PDF: the last
          completed month for monthly reports, the last completed quarter for
          quarterly reports, the last completed week for the rest. Also runs
          automatically every week (monthly reports once a month, quarterly
          reports once a quarter).
        </div>
      </div>

      {subscriptionMessage && (
        <div
          className={
            subscriptionIsError ? Style.errorMessage : Style.successMessage
          }
        >
          {subscriptionMessage}
        </div>
      )}

      <button
        type="button"
        disabled={isRunningSubscriptions}
        className={Style.submitButton}
        onClick={runWeeklySubscriptionEmails}
      >
        {isRunningSubscriptions
          ? 'Sending…'
          : 'Send Weekly Subscription Emails Now'}
      </button>

      <div className={Style.divider} />

      <div className={Style.section}>
        <div className={Style.subscriptionsHeader}>
          <label className={Style.label}>Current Subscriptions:</label>
          <button
            type="button"
            className={Style.linkButton}
            disabled={isLoadingSubscriptions}
            onClick={() => fetchSubscriptions()}
          >
            {isLoadingSubscriptions ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {subscriptions.length === 0 ? (
          <div className={Style.reportHint}>
            {isLoadingSubscriptions
              ? 'Loading subscriptions…'
              : 'No active subscriptions.'}
          </div>
        ) : (
          <div className={Style.tableWrapper}>
            <table className={Style.subscriptionsTable}>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Jurisdiction</th>
                  <th>Time Interval</th>
                  <th>Status</th>
                  <th>Subscribed</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map((subscription) => {
                  const isSending =
                    sendingSubscriptionId === subscription.id ||
                    isRunningSubscriptions;
                  const isUpdating = updatingSubscriptionId === subscription.id;
                  return (
                    <tr
                      key={subscription.id}
                      className={
                        subscription.paused || !subscription.confirmed
                          ? Style.pausedRow
                          : undefined
                      }
                    >
                      <td>{subscription.email}</td>
                      <td>{subscription.jurisdiction || '—'}</td>
                      <td>{subscription.periodicity || '—'}</td>
                      <td>
                        {!subscription.confirmed
                          ? 'Awaiting confirmation'
                          : subscription.paused
                            ? 'Paused'
                            : 'Active'}
                      </td>
                      <td>
                        {subscription.createdAt
                          ? new Date(
                              subscription.createdAt,
                            ).toLocaleDateString()
                          : '—'}
                      </td>
                      <td>
                        <div className={Style.subscriptionActions}>
                          <button
                            type="button"
                            className={Style.sendNowButton}
                            disabled={isSending || !subscription.confirmed}
                            onClick={() => sendSubscriptionNow(subscription)}
                          >
                            {sendingSubscriptionId === subscription.id
                              ? 'Sending…'
                              : 'Send now'}
                          </button>
                          <button
                            type="button"
                            className={Style.linkButton}
                            disabled={isUpdating || isSending}
                            onClick={() =>
                              toggleSubscriptionPaused(subscription)
                            }
                          >
                            {isUpdating
                              ? 'Saving…'
                              : subscription.paused
                                ? 'Resume'
                                : 'Pause'}
                          </button>
                          <button
                            type="button"
                            className={Style.dangerButton}
                            disabled={isUpdating || isSending}
                            onClick={() => setPendingDelete(subscription)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pendingDelete && (
        <ModalWrapper
          isOpen
          closeModal={closeDeleteModal}
          style={modalStyles}
          closeButtonColor="#1a1a1a"
        >
          <form
            className={Style.mailingListModal}
            onSubmit={confirmDeleteSubscription}
          >
            <h3 className={Style.modalTitle}>Delete subscription</h3>
            <p className={Style.modalCopy}>
              Delete the {pendingDelete.periodicity || ''} subscription for{' '}
              <strong>{pendingDelete.email}</strong>? They will stop receiving
              this report. This cannot be undone.
            </p>

            {deleteError && (
              <div className={Style.errorMessage}>{deleteError}</div>
            )}

            <div className={Style.modalActions}>
              <button
                type="button"
                className={Style.linkButton}
                onClick={closeDeleteModal}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={Style.modalDangerButton}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting…' : 'Delete subscription'}
              </button>
            </div>
          </form>
        </ModalWrapper>
      )}
    </div>
  );
};

export default AdminPage;
